import { Achievements, Endgame, battleMode } from "@highmarch/app";
import { battleFacts, starCount } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { guide } from "../ui/guide/guide";
import { achievementsNote, showAchievements } from "../ui/hub/achievementsSheet";
import { showAchievementBanner } from "../ui/hub/achievementBanner";
import { setCommanderHonours } from "../ui/hub/commanderSheet";
import { showAlmanac } from "../ui/hub/almanacSheet";
import type { Feature, HubApi } from "./registry";

/** More-sheet slot: after the Market (20), before the Almanac (40) (registry.ts MORE_ORDER). */
export const ACHIEVEMENTS_ORDER = 30;
/** What the banner waits behind: the away card's scrim and any sheet (check-in, More, the Armoury…). */
const BUSY_SELECTOR = ".awayscrim, .sheet";
/** The Guide topic opens once the hub does (after level 1). */
const TOPIC_FROM_LEVEL = 2;

/** The `achievement` cue (WP10): the unlock banner and a claim. */
function cue(): void {
  try { audio.play("achievement"); } catch { /* no audio in tests */ }
}

/** The run's key for the stats: level, seed and the tick it ended on (a run is counted once however it is reported). */
const runKey = (level: number, seed: bigint, tick: number): string => `${level}:${seed.toString(36)}:${tick}`;

/**
 * Achievements and lifetime stats (FINISH_PLAN WP7, M19; DESIGN §9.7): every battle's kills and damage into the
 * `stats` section as the level is left; More → Achievements with a red count for rewards to claim; the hub's
 * "Achievement unlocked" banner (with the `achievement` cue) the first time the hub sees one done; the Commander
 * sheet's honours card (title, count, the Almanac); a Guide topic.
 */
export const achievementsFeature: Feature = {
  id: "achievements",
  register(ctx, registry) {
    const a = new Achievements(ctx.catalog);
    const endgame = new Endgame(ctx.catalog);
    const open = (hub: HubApi): void => {
      a.sync(ctx.now());
      ctx.persist();
      showAchievements(hub.parent, { catalog: ctx.catalog, achievements: a, now: ctx.now, onChange: () => hub.changed(), onClaim: cue });
    };

    registry.battleEnd((b) => {
      const kind = endgame.runKind(b.sim.levelNumber, b.sim.modifiers) ?? "plain";
      // MP-27: a win is announced (battleWon) by the use case, once per run, with its mode and clear time
      const run = { mode: battleMode(ctx.catalog, b.sim.levelNumber, b.sim.modifiers, b.sim.firstClear), ticks: b.sim.tick, stars: starCount(b.stars ?? 0), now: ctx.now() };
      a.recordBattle(battleFacts(b.sim, b.counts, b.damageByTower, kind, b.assistsByTower), runKey(b.sim.levelNumber, b.sim.seed, b.sim.tick), run);
    });

    registry.moreRow({
      id: "achievements", order: ACHIEVEMENTS_ORDER, icon: "achievement",
      label: () => "Achievements",
      note: () => achievementsNote(a),
      dot: () => a.claimable(),
      open,
    });

    // The banner: checked on every hub render and tick, quiet while Captain Bram talks or a card or sheet is up. Newly done achievements
    // are marked seen as the banner shows them, so each is announced once.
    registry.hubWidget({
      id: "achievementBanner",
      mount: (hub) => {
        const check = (): void => {
          // not over Captain Bram, the away card or an open sheet: the banner waits for the plain hub
          if (guide()?.quiet() === false || hub.parent.querySelector(BUSY_SELECTOR) !== null) return;
          const fresh = a.sync(ctx.now());
          const unseen = a.progress.unseen(ctx.balance.achievements);
          if (unseen.length === 0) { if (fresh.length > 0) ctx.persist(); return; }
          a.progress.markSeen(unseen.map((d) => d.id));
          showAchievementBanner(hub.parent, unseen, () => open(hub));
          cue();
          hub.changed();                                  // the More badge counts the new claim
        };
        check();
        return { update: check, dispose: () => undefined };
      },
    });

    setCommanderHonours({
      achievements: a,
      openAchievements: (parent) => showAchievements(parent, { catalog: ctx.catalog, achievements: a, now: ctx.now, onChange: () => ctx.persist(), onClaim: cue }),
      openAlmanac: (parent) => showAlmanac(parent, { catalog: ctx.catalog, achievements: a }),
    });

    registry.guideTopic({
      id: "achievements", group: "castle", label: "Achievements & Almanac", art: { icon: "trophy" }, fromLevel: TOPIC_FROM_LEVEL, steps: [],
      lines: [
        "**Achievements** are long goals: clear chapters, earn stars, beat every boss, master a tower. Each pays once: claim it in the **⚙ menu → Achievements**.",
        "Some unlock a **commander title**. Pick the one you wear on the Achievements sheet.",
        "The **Almanac** (⚙ → Almanac) keeps every raider you've met and what it is **weak to**, and each tower's **mastery**: its lifetime kills light a bronze, silver and gold trim.",
      ],
    });
  },
};
